import type {
  AdminAccountResponse,
  AdminApproveGalleryUpgradeRequest,
  AdminArtworkProvenancePage,
  AdminArtworkResponse,
  AdminCreateProvenanceDraftRequest,
  AdminEditProvenanceRequest,
  AdminGalleryResponse,
  AdminGalleryUpgradeRequestResponse,
  AdminProvenanceHistory,
  AdminProvenanceReviewItem,
  AdminRejectGalleryUpgradeRequest,
  AdminRequestDetailResponse,
  AdminRequestInboxItemResponse,
  AdminRequestInboxParams,
  AdminReviewRequest,
  AdminStatsResponse,
  AdminTransferResponse,
  AdminUserResponse,
  CreateModerationArtworkRequest,
  ModerationArtworkResponse,
  PaginatedParams,
  ProvenanceResponse,
} from '@/types';
import { httpClient } from './httpClient';

const BASE = '/api/art';

// Artworks

export async function getAdminArtworks(
  params?: PaginatedParams & { status?: string },
): Promise<AdminArtworkResponse[]> {
  const response = await httpClient.get<AdminArtworkResponse[]>(`${BASE}/admin/artworks`, {
    params,
  });

  return response.data;
}

// Sends JSON without photos. With photos it sends multipart: the JSON in field
// "artwork" and the files, in order, in field "photos". The server saves both or nothing.
export async function createModerationArtwork(
  data: CreateModerationArtworkRequest,
  photos: File[] = [],
): Promise<ModerationArtworkResponse> {
  const url = `${BASE}/admin/moderation/artworks`;
  if (photos.length === 0) {
    const response = await httpClient.post<ModerationArtworkResponse>(url, data);

    return response.data;
  }

  const formData = new FormData();
  formData.append('artwork', JSON.stringify(data));
  for (const photo of photos) {
    formData.append('photos', photo);
  }
  const response = await httpClient.post<ModerationArtworkResponse>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
}

export async function getAdminUsers(params?: PaginatedParams): Promise<AdminUserResponse[]> {
  const response = await httpClient.get<AdminUserResponse[]>(`${BASE}/admin/users`, { params });

  return response.data;
}

// Accounts (with roles)

export async function getAdminAccounts(
  params?: PaginatedParams & { email?: string },
): Promise<AdminAccountResponse[]> {
  const response = await httpClient.get<AdminAccountResponse[]>(`${BASE}/admin/accounts`, {
    params,
  });

  return response.data;
}

export async function getAdminGalleries(params?: PaginatedParams): Promise<AdminGalleryResponse[]> {
  const response = await httpClient.get<AdminGalleryResponse[]>(`${BASE}/admin/galleries`, {
    params,
  });

  return response.data;
}

export async function getAdminGallery(id: string): Promise<AdminGalleryResponse> {
  const response = await httpClient.get<AdminGalleryResponse>(`${BASE}/admin/galleries/${id}`);

  return response.data;
}

export async function verifyGallery(id: string): Promise<AdminGalleryResponse> {
  const response = await httpClient.patch<AdminGalleryResponse>(
    `${BASE}/admin/galleries/${id}/verify`,
  );

  return response.data;
}

export async function unverifyGallery(id: string): Promise<AdminGalleryResponse> {
  const response = await httpClient.patch<AdminGalleryResponse>(
    `${BASE}/admin/galleries/${id}/unverify`,
  );

  return response.data;
}

// Transfers

export async function getAdminTransfers(
  params?: PaginatedParams,
): Promise<AdminTransferResponse[]> {
  const response = await httpClient.get<AdminTransferResponse[]>(`${BASE}/admin/transfers`, {
    params,
  });

  return response.data;
}

// Stats

export async function getAdminStats(): Promise<AdminStatsResponse> {
  const response = await httpClient.get<AdminStatsResponse>(`${BASE}/admin/stats`);

  return response.data;
}

// Provenance management

export async function getProvenanceReviews(
  params?: PaginatedParams,
): Promise<AdminProvenanceReviewItem[]> {
  const response = await httpClient.get<AdminProvenanceReviewItem[]>(
    `${BASE}/admin/provenance/reviews`,
    { params },
  );

  return response.data;
}

export async function approveProvenance(
  recordId: string,
  data: AdminReviewRequest,
): Promise<ProvenanceResponse> {
  const response = await httpClient.patch<ProvenanceResponse>(
    `${BASE}/admin/provenance/${recordId}/approve`,
    data,
  );

  return response.data;
}

export async function sendBackProvenance(
  recordId: string,
  data: AdminReviewRequest,
): Promise<ProvenanceResponse> {
  const response = await httpClient.patch<ProvenanceResponse>(
    `${BASE}/admin/provenance/${recordId}/send-back`,
    data,
  );

  return response.data;
}

export async function rejectProvenanceFinal(
  recordId: string,
  data: AdminReviewRequest,
): Promise<ProvenanceResponse> {
  const response = await httpClient.patch<ProvenanceResponse>(
    `${BASE}/admin/provenance/${recordId}/reject`,
    data,
  );

  return response.data;
}

export async function getProvenanceHistory(recordId: string): Promise<AdminProvenanceHistory> {
  const response = await httpClient.get<AdminProvenanceHistory>(
    `${BASE}/admin/provenance/${recordId}/history`,
  );

  return response.data;
}

export async function getAdminArtworkProvenance(
  artworkId: string,
  cursor?: string,
): Promise<AdminArtworkProvenancePage> {
  const response = await httpClient.get<AdminArtworkProvenancePage>(
    `${BASE}/admin/provenance/artworks/${artworkId}`,
    { params: cursor ? { cursor } : undefined },
  );

  return response.data;
}

export async function editAdminProvenance(
  recordId: string,
  data: AdminEditProvenanceRequest,
): Promise<ProvenanceResponse> {
  const response = await httpClient.patch<ProvenanceResponse>(
    `${BASE}/admin/provenance/${recordId}`,
    data,
  );

  return response.data;
}

export async function withdrawAdminProvenance(
  recordId: string,
  reason: string,
): Promise<ProvenanceResponse> {
  const response = await httpClient.post<ProvenanceResponse>(
    `${BASE}/admin/provenance/${recordId}/withdraw`,
    { reason },
  );

  return response.data;
}

export async function createAdminProvenanceDraft(
  data: AdminCreateProvenanceDraftRequest,
): Promise<ProvenanceResponse> {
  const response = await httpClient.post<ProvenanceResponse>(
    `${BASE}/admin/provenance/drafts`,
    data,
  );

  return response.data;
}

export async function submitAdminProvenanceDraft(recordId: string): Promise<ProvenanceResponse> {
  const response = await httpClient.post<ProvenanceResponse>(
    `${BASE}/admin/provenance/drafts/${recordId}/submit`,
  );

  return response.data;
}

// Gallery upgrade requests (admin)

export async function getAdminGalleryUpgradeRequests(
  params?: PaginatedParams & { status?: string },
): Promise<AdminGalleryUpgradeRequestResponse[]> {
  const response = await httpClient.get<AdminGalleryUpgradeRequestResponse[]>(
    `${BASE}/admin/gallery-requests`,
    { params },
  );

  return response.data;
}

// Centralized request inbox (admin)

export async function getAdminRequestInbox(
  params?: AdminRequestInboxParams,
): Promise<AdminRequestInboxItemResponse[]> {
  const response = await httpClient.get<AdminRequestInboxItemResponse[]>(`${BASE}/admin/requests`, {
    params,
  });

  return response.data;
}

export async function getAdminRequestDetail(id: string): Promise<AdminRequestDetailResponse> {
  const response = await httpClient.get<AdminRequestDetailResponse>(`${BASE}/admin/requests/${id}`);

  return response.data;
}

export async function getAdminGalleryUpgradeRequest(
  id: string,
): Promise<AdminGalleryUpgradeRequestResponse> {
  const response = await httpClient.get<AdminGalleryUpgradeRequestResponse>(
    `${BASE}/admin/gallery-requests/${id}`,
  );

  return response.data;
}

export async function approveGalleryUpgradeRequest(
  id: string,
  data: AdminApproveGalleryUpgradeRequest,
): Promise<AdminGalleryUpgradeRequestResponse> {
  const response = await httpClient.patch<AdminGalleryUpgradeRequestResponse>(
    `${BASE}/admin/gallery-requests/${id}/approve`,
    data,
  );

  return response.data;
}

export async function rejectGalleryUpgradeRequest(
  id: string,
  data: AdminRejectGalleryUpgradeRequest,
): Promise<AdminGalleryUpgradeRequestResponse> {
  const response = await httpClient.patch<AdminGalleryUpgradeRequestResponse>(
    `${BASE}/admin/gallery-requests/${id}/reject`,
    data,
  );

  return response.data;
}
